import { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomInput from "../CustomInput";
import { Button } from "react-bootstrap";
import useHandleSuccess from "../../../utils/parent/useHandleSuccess";
import { useResendVerification } from "../../../hooks/authV2/useResendVerification";

// Captcha trigger component inside login form
export function CaptchaTrigger({ setCaptchaValue }) {
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    let isMounted = true;
    let retries = 8;

    const waitForRecaptcha = async () => {
      if (!executeRecaptcha) {
        if (retries > 0) {
          retries--;
          setTimeout(waitForRecaptcha, 700); // Increase delay
        }
        return;
      }
      try {
        const token = await executeRecaptcha("login");
        if (token && isMounted) {
          setCaptchaValue(true);
        }
      } catch (err) {
        //consoleerror("Recaptcha execution error:", err);
      }
    };

    waitForRecaptcha();

    return () => {
      isMounted = false;
    };
  }, [executeRecaptcha, setCaptchaValue]);

  return null;
}

export const AuthLoginEmail = ({ setView, onHide }) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const apiUrl = process.env.REACT_APP_API_URL;
  const [captchaValue, setCaptchaValue] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const {
    loading: resendLoading,
    message: resendMessage,
    setMessage: setResendMessage,
    resendVerification,
  } = useResendVerification();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect") || "";
  const tab = searchParams.get("tab") || "";
  const { handleSuccess } = useHandleSuccess();

  const initialValues = {
    username: "",
    password: "",
    rememberMe: false,
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string().required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const fireLoginEvent = (userObj, method, roleLabel) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "login",
      method,
      user_email: userObj?.email || "",
      user_role: roleLabel || userObj?.role || "",
      user_name: userObj?.first_name || userObj?.name || "",
    });
  };
  const handleSuccessFun = async (data) => {
    await handleSuccess({ data, redirect, tab, navigate });
    onHide();
  };

  const handleLogin = async (values) => {
    setLoading(true);
    setFormError("");
    localStorage.removeItem("user");
    localStorage.removeItem("student");

    const payload = {
      password: values.password,
      remember_me: values.rememberMe,
    };

    if (values.username.includes("@")) {
      payload.email = values.username;
    } else {
      payload.username = values.username;
    }


    try {
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMsg = "Something went wrong";
        try {
          const errorData = await response.json();

          // Unverified user
          if (response.status === 403 && errorData?.email_verified === false) {
            setUnverifiedEmail(values.username);
            return;
          }

          if (["User not found", "Unauthorized"].includes(errorData?.message)) {
            errorMsg = "Login failed. Please check your credentials.";
          } else {
            errorMsg = errorData?.message || errorMsg;
          }
        } catch {
          try {
            errorMsg = await response.text();
          } catch { }
        }
        setFormError(errorMsg);
        return;
      }

      const data = await response.json();
      await handleSuccessFun(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = () => {
    resendVerification({ email: unverifiedEmail });
  };

  return (
    <>
      {unverifiedEmail ? (
        <>
          {/* <div className="auth-verify-icon">
            <i className="fa fa-envelope-open" />
          </div> */}

          <div className="auth-modal-title">Verify Your Email</div>

          <p className="auth-forgot__subtitle">
            Your account has not been verified yet. Please check your inbox for the verification email sent to your registered email address.
          </p>
          <p className="auth-forgot__email">{unverifiedEmail}</p>

          {resendMessage.text && (
            <div
              className={`auth-verify-msg auth-verify-msg--${resendMessage.type}`}
            >
              {resendMessage.text}
            </div>
          )}

          <Button
            className="custom-primary-button mt-2"
            onClick={handleResendVerification}
            disabled={resendLoading}
          >
            {resendLoading ? "Sending..." : "Resend Verification Email"}
          </Button>

          <div
            className="auth-forgot__backBtn"
            onClick={() => {
              setUnverifiedEmail(null);
              setResendMessage({ type: "", text: "" });
            }}
          >
            <i className="bi bi-arrow-left" /> Back to Login
          </div>
        </>
      ) : (
        <>
          <div className="auth-modal-title">Login to your account</div>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleLogin}
          >
            {({ isSubmitting }) => (
              <Form>
                <div className="auth-form-group">
                  <CustomInput
                    label="Username / Email"
                    name="username"
                    type="text"
                    placeholder="Enter your email"
                    showAstreisk={false}
                  />
                </div>

                <div className="auth-form-group">
                  <CustomInput
                    label="Password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    showAstreisk={false}
                  />
                  <span
                    className="auth-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i
                      className={`fa ${showPassword ? "fa-eye" : "fa-eye-slash"}`}
                    ></i>
                  </span>
                </div>

                <div className="mb-3 form-check d-flex align-items-center justify-content-between" style={{ fontSize: "0.9rem" }}>
                  <div className="d-flex align-items-center">
                    <Field
                      type="checkbox"
                      name="rememberMe"
                      className="form-check-input mt-0"
                      id="rememberMe"
                      autoComplete="rememberMe"
                    />
                    <label
                      className="form-check-label ms-2"
                      htmlFor="rememberMe"
                      style={{ cursor: "pointer", color: "#3a3a3a" }}
                    >
                      Remember me
                    </label>
                  </div>
                  <label
                    className="text-end mb-0 authLink"
                    onClick={() => setView("forgotPassword")}
                  >
                    Forgot Password ?
                  </label>
                </div>

                {formError && (
                  <div className="text-danger text-center mb-3">{formError}</div>
                )}

                <CaptchaTrigger setCaptchaValue={setCaptchaValue} />

                <Button
                  type="submit"
                  className="custom-primary-button mt-2"
                  disabled={loading || isSubmitting || !captchaValue}
                >
                  {loading ? "Logging in..." : "Log In"}
                </Button>

                <div className="text-center mt-3" style={{ fontSize: "0.95rem", color: "#3a3a3a" }}>
                  Don't have an account?{" "}
                  <span
                    onClick={() => setView("register")}
                    className="authLink"
                  >
                    Sign Up
                  </span>
                </div>
              </Form>
            )}
          </Formik>
        </>
      )}
    </>
  );
};
